import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { formatSeconds } from '../../utils/apneaTables';
import { ApiSessionType, ApneaSessionDto } from '../../services/apneaService';

/**
 * Session history.
 *
 * Ordered newest first and framed around trends rather than individual numbers —
 * the training book is explicit that the tracker exists to spot trends, not to
 * judge single sessions. So recovery and first-contraction timing get columns of
 * their own, and longest hold is deliberately not emphasised.
 */

const TYPE_LABEL: Record<ApiSessionType, string> = {
  Co2: 'CO₂ table',
  O2: 'O₂ table',
  NoContraction: 'No-contraction',
  ApneaWalk: 'Apnea walk',
  DryStatic: 'Max static',
  Bolt: 'BOLT',
  Breathing: 'Breathing',
};

interface SessionHistoryProps {
  sessions: ApneaSessionDto[];
}

const bestContraction = (session: ApneaSessionDto): number | null => {
  const marks = session.actualSets
    .map(s => s.firstContractionAt)
    .filter((v): v is number => typeof v === 'number');
  return marks.length > 0 ? Math.max(...marks) : null;
};

const SessionHistory: React.FC<SessionHistoryProps> = ({ sessions }) => {
  const [typeFilter, setTypeFilter] = useState<ApiSessionType | 'all'>('all');

  const filtered = useMemo(
    () => sessions
      .filter(s => typeFilter === 'all' || s.type === typeFilter)
      .slice()
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()),
    [sessions, typeFilter]
  );

  const presentTypes = useMemo(
    () => Array.from(new Set(sessions.map(s => s.type))),
    [sessions]
  );

  if (sessions.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Session history</Typography>
        <Typography variant="body2" color="text.secondary">
          Nothing logged yet. A short log that gets completed beats a perfect one that does not.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant="subtitle1">Session history</Typography>
        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value as ApiSessionType | 'all')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All types</MenuItem>
          {presentTypes.map(t => (
            <MenuItem key={t} value={t}>{TYPE_LABEL[t]}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Sets</TableCell>
              <TableCell align="center">Recovery</TableCell>
              <TableCell align="center">1st contraction</TableCell>
              <TableCell>Focus</TableCell>
              <TableCell align="center">Decision</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(session => {
              const contraction = bestContraction(session);
              return (
                <TableRow key={session.sortKey} hover>
                  <TableCell>
                    {new Date(session.startedAt).toLocaleDateString(undefined, {
                      month: 'short', day: 'numeric',
                    })}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2">{TYPE_LABEL[session.type]}</Typography>
                      {session.phase && (
                        <Chip size="small" variant="outlined" label={`P${session.phase}`} />
                      )}
                      {session.aborted && <Chip size="small" color="info" label="early" />}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">{session.actualSets.length}</TableCell>
                  <TableCell align="center">
                    {session.metrics.recovery ? (
                      <Chip
                        size="small"
                        label={session.metrics.recovery}
                        color={session.metrics.recovery <= 2 ? 'warning' : 'success'}
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell align="center">
                    {contraction !== null ? formatSeconds(contraction) : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {session.focus || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">{session.decision || '—'}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {sessions.some(s => s.warningSigns?.occurred) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Some sessions recorded warning signs. Review what preceded them before
          increasing any workload.
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Look for trends across sessions rather than judging any single one. Later
        first-contraction times and steady recovery scores are the signals that matter.
      </Typography>
    </Paper>
  );
};

export default SessionHistory;
